import { FC, useEffect, useState } from "react"
import * as Yup from "yup"
import { Form, Formik } from "formik"

import { Modal, ModalProps } from "@components/modal"
import AuthServices from "@services/auth.services"
import { setCookie } from "nookies"
import { domainChecker } from "@lib/functions"
import { RegularButton, GoogleLoginButton } from "@components/button"
import { FlexBox } from "@components/wrapper"
import { FormikInput, PasswordInput } from "@components/input/formik"
import Link from "next/link"

export interface LoginModalProps extends ModalProps {
  redirect?: string
}

export const LoginModal: FC<LoginModalProps> = ({ show, onClose, redirect, ...props }) => {
  const [validation, setValidation] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [validationRequired, setValidationRequired] = useState(false)

  useEffect(() => {
    if (!show) {
      setValidation("")
    }
  }, [show])

  const auth = new AuthServices()

  const validationSchema = Yup.object({
    email: Yup.string().email("Email tidak valid").required("Email harus diisi"),
    password: Yup.string().required("Password harus diisi"),
  })

  function errorMessageGenerator(msg: string) {
    setValidation(msg)
  }

  return (
    <Modal {...props} show={show} onClose={onClose} contentClassName="!p-0 lg:w-[30%]">
      <h2 className="pt-5 font-semibold text-center text-2xl">Login</h2>

      <div className="pt-[30px] px-[30px]">
        <GoogleLoginButton
          handleError={errorMessageGenerator}
          redirect={redirect}
          id="btn-login-with-google"
        />
        <div className="h-3 my-10 text-center border-b-grey-97 border-b">
          <span className="bg-white px-3.5">atau</span>
        </div>

        <Formik
          initialValues={{ email: "", password: "" }}
          validationSchema={validationSchema}
          validateOnChange={validationRequired}
          validateOnBlur={validationRequired}
          onSubmit={async (value) => {
            setValidation("")
            setIsLoading(true)
            try {
              const res = await auth.manualLogin(value)

              if (res.isFailure) {
                const err = res.error
                setValidation(err.message)
                setIsLoading(false)
              } else {
                const result = res.getValue()

                if (!redirect) {
                    window.location.reload()
                  } else {
                    window.location.assign(redirect)
                  }
              }
            } catch (e: any) {
              console.log(e)
              setValidation("Terjadi kesalahan. Cek kembali data yang diinput dan coba lagi")
              setIsLoading(false)
            }
          }}
        >
          <Form>
            <FormikInput
              placeholder="Masukkan e-mail kamu"
              name="email"
              className="mb-6"
              id="login-email-field"
            />

            <PasswordInput
              placeholder="Masukkan password kamu"
              name="password"
              className="mb-6"
              id="login-email-password"
              showButtonId="hide-show-password-login"
            />

            <RegularButton
              className="mt-6 w-full py-3.5 rounded"
              isSubmitting={isLoading}
              onClick={() => setValidationRequired(true)}
              isFormik
              id="btn-login-modal"
            >
              Masuk
            </RegularButton>
          </Form>
        </Formik>

        <div className="mt-2.5 text-red">{validation}</div>
        <div className="mt-4 mb-5 text-center">
          <Link
            href={"/lupa-password"}
            className="text-green font-semibold text-center"
            id="btn-forget-password-modal"
          >
            Lupa Password?
          </Link>
        </div>
      </div>

      <FlexBox className="p-6 border-t-[#dee2e6] border-t" justify="center">
        Belum bergabung?{" "}
        <a href={`/register`} className="ml-1.5 text-green font-semibold" id="btn-regist-modal">
          Daftar Akun
        </a>
      </FlexBox>
    </Modal>
  )
}
